import { useLocation } from 'react-router-dom';
import { deriveMode } from '@/shared/mode/deriveMode';
import { type Mode } from '@/shared/mode/types';
import { useStore } from '@/shared/state/store';
import { Pane } from './Pane';
import { InstaflixRoutes } from '@/modes/instaflix/routes';
import { ReelsflixRoutes } from '@/modes/reelsflix/routes';
import { MovieflixRoutes } from '@/modes/movieflix/routes';
import { Settings } from '@/settings/Settings';

/**
 * PersistentPanes — keeps all 3 mode trees mounted across mode switches.
 *
 * Each mode's <Routes> is given an "effective location":
 *   - active mode: real URL (so in-mode navigation works)
 *   - inactive mode: that mode's last-known URL from tabHistory
 *     (so its previously-rendered screen stays mounted)
 *
 * Without this, an inactive mode's <Routes> would re-evaluate against the
 * current URL, fail to match any of its in-mode routes, and unmount the
 * previously-rendered screen — defeating the persistence promise.
 */
export function PersistentPanes() {
  const realLocation = useLocation();
  const tabHistory = useStore((s) => s.tabHistory);
  const active = deriveMode(realLocation.pathname);

  if (realLocation.pathname.startsWith('/settings')) {
    return <Settings />;
  }

  const effective = (mode: Mode): Partial<Location> | string => {
    if (active === mode) return realLocation;
    const stack = tabHistory[mode];
    return stack && stack.length > 0 ? stack[stack.length - 1] : `/${mode}`;
  };

  return (
    <>
      <Pane active={active === 'instaflix'} mode="instaflix" testId="pane-instaflix">
        <InstaflixRoutes location={effective('instaflix')} />
      </Pane>
      <Pane active={active === 'reelsflix'} mode="reelsflix" testId="pane-reelsflix">
        <ReelsflixRoutes location={effective('reelsflix')} />
      </Pane>
      <Pane active={active === 'movieflix'} mode="movieflix" testId="pane-movieflix">
        <MovieflixRoutes location={effective('movieflix')} />
      </Pane>
    </>
  );
}
